/**
 * Chat Agent Chat Page.
 * Full-width chat interface for conversing with a LibreChat agent.
 * Reuses the platform's Chat, ChatMessage, and ChatAgentInput components.
 * Streams responses via SSE directly from LibreChat.
 */

import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import {
  Settings,
  History,
  X,
  Plus,
  Maximize2,
  ArrowDown,
  Trash2,
  Check,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useNotification } from '@/components/notifications';
import { v4 as uuidv4 } from 'uuid';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { useChatAgentsStore } from '@/stores/chat-agents-store';
import {
  getChatAgent,
  getAgentChatHistory,
  listAgentConversations,
  getConversationMessages,
  deleteConversation,
} from '@/services/chat-agents/agents';
import { streamAgentChat } from '@/services/chat-agents/chat';
import { ChatAgentInput } from '@/components/chat/chat-agent-input';
import { useGetCollections } from '@/hooks/queries/data-sources/use-get-collections';
import {
  referenceKey,
  toCollectionReferences,
  toReferenceNameMap,
  type CollectionReference,
} from '@/utils/collection-references';
import {
  CustomInstructionsPopover,
  loadCustomInstructions,
  persistCustomInstructions,
} from './components/custom-instructions-popover';
import { ChatMessage as ChatMessageComponent } from '@/components/chat/chat-message';
import { ToolProgressIndicator } from '@/components/chat/tool-progress-indicator';
import { ChatHistorySidebar } from '@/components/chat/chat-history-sidebar';
import type { ChatMessageData } from '@/components/chat/types';
import type { ChatMessage } from '@/types/chat-agent-types';

/** Clean an MCP tool name: strip _mcp_server-name suffix and replace underscores. */
function cleanToolName(name: string): string {
  return name.includes('_mcp_')
    ? name.split('_mcp_')[0].replace(/_/g, ' ')
    : name.replace(/_/g, ' ');
}

/** Extract a short human-readable detail from tool arguments. */
function extractToolDetail(args: string): string {
  if (!args) return '';
  try {
    const parsed = JSON.parse(args);
    if (parsed.prompt) {
      const p = parsed.prompt as string;
      return p.length > 100 ? `${p.slice(0, 100)}...` : p;
    }
    if (parsed.filename) return parsed.filename;
    if (parsed.query) {
      const q = parsed.query as string;
      return q.length > 100 ? `${q.slice(0, 100)}...` : q;
    }
    if (parsed.node_id) return parsed.node_id;
  } catch {
    /* ignore */
  }
  return '';
}

/** Resize an image to fit within maxW x maxH and compress as JPEG */
function resizeImage(
  file: File,
  maxW: number,
  maxH: number,
  quality: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxW || height > maxH) {
        const ratio = Math.min(maxW / width, maxH / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No canvas context'));
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

interface ChatAgentChatPageProps {
  agentIdOverride?: string;
  showSettings?: boolean;
  showInlineHistory?: boolean;
  /** Content to render below the chat input in the empty state (e.g., template buttons) */
  emptyStateBelowInput?: React.ReactNode;
  /** Custom heading to replace the default agent name + description in empty state */
  emptyStateHeading?: React.ReactNode;
  /** Start with a fresh chat instead of loading the most recent conversation */
  startFresh?: boolean;
  /** Custom title for the inline history sidebar */
  sidebarTitle?: React.ReactNode;
}

export function ChatAgentChatPage({
  agentIdOverride,
  showSettings = true,
  showInlineHistory = false,
  emptyStateBelowInput,
  emptyStateHeading,
  startFresh = false,
  sidebarTitle,
}: ChatAgentChatPageProps = {}) {
  const { agentId: urlAgentId } = useParams<{ agentId: string }>();
  const agentId = agentIdOverride || urlAgentId;
  const navigate = useOrgNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const {
    currentAgent,
    setCurrentAgent,
    messages,
    addMessage,
    updateLastMessage,
    addToolCallToLastMessage,
    resolveToolCallOnLastMessage,
    settleRunningToolCalls,
    setMessageDone,
    clearMessages,
    isStreaming,
    setStreaming,
    activeToolCall,
    setActiveToolCall,
    conversationId,
    setConversationId,
    parentMessageId,
    setParentMessageId,
    conversations,
    setConversations,
    removeConversation,
    isHistoryOpen,
    setHistoryOpen,
  } = useChatAgentsStore();

  const notification = useNotification();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollListenerRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const skipSmoothScrollRef = useRef(false);

  // Open history panel by default when inline history is enabled
  useEffect(() => {
    if (showInlineHistory) {
      setHistoryOpen(true);
    }
  }, [showInlineHistory, setHistoryOpen]);

  const [attachedImages, setAttachedImages] = useState<
    Array<{ file: File; preview: string }>
  >([]);

  // Attached Data Collections are consumed by the turn they are sent with. The
  // agent keeps seeing them through the [DATA COLLECTIONS] block on that
  // message, so re-arming the composer would only duplicate the chip.
  const [attachedReferences, setAttachedReferences] = useState<
    CollectionReference[]
  >([]);

  const { data: collections, isLoading: isLoadingCollections } =
    useGetCollections({ orgId: currentOrg?.orgid });

  const collectionReferences = useMemo(
    () => toCollectionReferences(collections ?? []),
    [collections],
  );

  const collectionNames = useMemo(
    () => toReferenceNameMap(collectionReferences),
    [collectionReferences],
  );

  const attachReference = useCallback((reference: CollectionReference) => {
    setAttachedReferences((prev) =>
      prev.some((r) => referenceKey(r) === referenceKey(reference))
        ? prev
        : [...prev, reference],
    );
  }, []);

  const removeReference = useCallback((key: string) => {
    setAttachedReferences((prev) =>
      prev.filter((r) => referenceKey(r) !== key),
    );
  }, []);

  const [customInstructions, setCustomInstructions] = useState<string>(
    loadCustomInstructions,
  );

  const saveCustomInstructions = useCallback((value: string) => {
    setCustomInstructions(value);
    persistCustomInstructions(value);
  }, []);

  // Check if user is near the bottom of the scroll container
  const checkIsAtBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 80;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distance < threshold);
  }, []);

  // Callback ref — attaches scroll listener the moment the DOM element mounts
  const scrollContainerCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous listener
      if (scrollListenerRef.current && scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener(
          'scroll',
          scrollListenerRef.current,
        );
        scrollListenerRef.current = null;
      }
      scrollContainerRef.current = node;
      if (node) {
        node.addEventListener('scroll', checkIsAtBottom, { passive: true });
        scrollListenerRef.current = checkIsAtBottom;
        // Initial check
        checkIsAtBottom();
      }
    },
    [checkIsAtBottom],
  );

  // Auto-scroll only when user is near the bottom
  useEffect(() => {
    if (isAtBottom) {
      const behavior = skipSmoothScrollRef.current ? 'instant' : 'smooth';
      messagesEndRef.current?.scrollIntoView({ behavior });
      skipSmoothScrollRef.current = false;
    }
  }, [messages, activeToolCall, isAtBottom]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsAtBottom(true);
  }, []);

  // Fetch conversation list for the sidebar
  const refreshConversations = useCallback(async () => {
    if (!currentOrg?.orgid || !agentId) return;
    try {
      const convos = await listAgentConversations(currentOrg.orgid, agentId);
      setConversations(convos);
    } catch {
      // ignore
    }
  }, [currentOrg?.orgid, agentId, setConversations]);

  // Load a specific conversation by ID
  const handleSelectConversation = useCallback(
    async (selectedConversationId: string) => {
      if (!currentOrg?.orgid || !agentId || isStreaming) return;
      skipSmoothScrollRef.current = true;
      setIsLoadingHistory(true);
      clearMessages();
      setAttachedReferences([]);
      try {
        const data = await getConversationMessages(
          currentOrg.orgid,
          agentId,
          selectedConversationId,
        );
        if (data && data.messages.length > 0) {
          for (const msg of data.messages) {
            addMessage({
              id: msg.id,
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: msg.timestamp,
            });
          }
          setConversationId(data.conversationId);
          if (data.parentMessageId) {
            setParentMessageId(data.parentMessageId);
          }
        }
      } catch (e) {
        console.error('Failed to load conversation:', e);
      } finally {
        setIsLoadingHistory(false);
        // Jump to bottom instantly after messages render
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          setIsAtBottom(true);
        });
      }
    },
    [
      currentOrg?.orgid,
      agentId,
      isStreaming,
      clearMessages,
      addMessage,
      setConversationId,
      setParentMessageId,
    ],
  );

  // Load agent and LibreChat token
  useEffect(() => {
    const init = async () => {
      if (!currentOrg?.orgid || !agentId) return;
      skipSmoothScrollRef.current = true;
      setIsLoading(true);

      try {
        // Load agent details
        const agent = await getChatAgent(currentOrg.orgid, agentId);
        if (agent) {
          setCurrentAgent(agent);
        }

        // Load chat history for this agent (most recent conversation)
        if (!startFresh) {
          try {
            const history = await getAgentChatHistory(
              currentOrg.orgid,
              agentId,
            );
            if (history && history.messages.length > 0) {
              for (const msg of history.messages) {
                addMessage({
                  id: msg.id,
                  role: msg.role as 'user' | 'assistant',
                  content: msg.content,
                  timestamp: msg.timestamp,
                });
              }
              if (history.conversationId) {
                setConversationId(history.conversationId);
              }
              if (history.parentMessageId) {
                setParentMessageId(history.parentMessageId);
              }
            }
          } catch {
            // No history available, start fresh
          }
        }

        // Load conversation list for the sidebar
        await refreshConversations();
      } catch (e) {
        console.error('Failed to initialize chat:', e);
      } finally {
        setIsLoading(false);
        // Jump to bottom instantly after messages render
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          setIsAtBottom(true);
        });
      }
    };

    // Clear messages when switching to a different agent
    clearMessages();
    setAttachedReferences([]);
    init();
    return () => {
      abortControllerRef.current?.abort();
      // Revoke any attached image preview URLs to prevent blob leaks
      setAttachedImages((prev) => {
        prev.forEach((img) => URL.revokeObjectURL(img.preview));
        return [];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.orgid, agentId]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!currentAgent || isStreaming) return;

      // Chat goes through platform-api proxy (handles LibreChat auth server-side)

      // Resize and compress images before encoding to avoid token limits
      const imageBase64List: string[] = [];
      console.log('[ChatAgent] Attached images:', attachedImages.length);
      for (const img of attachedImages) {
        try {
          const resized = await resizeImage(img.file, 800, 800, 0.7);
          imageBase64List.push(resized);
          console.log(
            '[ChatAgent] Encoded image:',
            `(${resized.length} chars, ~${Math.round(resized.length / 1024)}KB)`,
          );
        } catch (err) {
          console.error('[ChatAgent] Failed to encode image:', err);
        }
      }
      console.log('[ChatAgent] Total encoded images:', imageBase64List.length);

      // Build display content with image previews
      let displayContent = content;
      if (attachedImages.length > 0) {
        const imageMarkdown = attachedImages
          .map((img) => `![${img.file.name}](${img.preview})`)
          .join('\n');
        displayContent = `${imageMarkdown}\n\n${content}`;
      }

      // Add user message
      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: 'user',
        content: displayContent,
        timestamp: new Date().toISOString(),
        ...(customInstructions ? { customInstructions } : {}),
        ...(attachedReferences.length > 0
          ? { collections: attachedReferences }
          : {}),
      };
      addMessage(userMessage);

      // Clear attached images
      attachedImages.forEach((img) => URL.revokeObjectURL(img.preview));
      setAttachedImages([]);

      // The chips now live on the sent message; the request below still uses
      // the references captured in this closure.
      setAttachedReferences([]);

      // Add placeholder assistant message
      const assistantMessageId = uuidv4();
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
      };
      addMessage(assistantMessage);

      setStreaming(true);
      abortControllerRef.current = new AbortController();

      const messageWithInstructions = customInstructions
        ? `[CUSTOM INSTRUCTIONS]\n${customInstructions}\n[/CUSTOM INSTRUCTIONS]\n\n${content}`
        : content;

      await streamAgentChat({
        agentId: currentAgent.id,
        message: messageWithInstructions,
        images: imageBase64List.length > 0 ? imageBase64List : undefined,
        collections:
          attachedReferences.length > 0 ? attachedReferences : undefined,
        conversationId: conversationId || undefined,
        parentMessageId: parentMessageId || undefined,
        onToken: (token) => {
          // Clear tool progress indicator once text starts streaming
          setActiveToolCall(null);
          updateLastMessage(token);
        },
        onThinking: () => {
          // Thinking tokens are accumulated silently - not shown in message
        },
        onToolCall: (name, args) => {
          const cName = cleanToolName(name);
          const detail = extractToolDetail(args);

          // Set active tool call for the progress indicator
          setActiveToolCall({
            name,
            cleanName: cName,
            args: detail,
            startTime: Date.now(),
          });

          // Track the call on the message — all calls of this turn render
          // as one rolled-up card instead of a scrolling line per call
          addToolCallToLastMessage({
            name: cName,
            detail: detail
              ? `${detail.slice(0, 80)}${detail.length > 80 ? '...' : ''}`
              : undefined,
            status: 'running',
          });
        },
        onToolResult: (name, result) => {
          // Clear the active tool call — progress indicator disappears
          setActiveToolCall(null);

          const cName = cleanToolName(name);
          let summary = '';
          let failed = false;
          let url: string | undefined;
          try {
            const parsed = JSON.parse(result);
            if (parsed.success !== undefined && !parsed.success) {
              failed = true;
              summary = parsed.error || 'unknown error';
            }
            if (parsed.workflow_url) url = parsed.workflow_url;
            if (parsed.image_url)
              summary += `${summary ? ' · ' : ''}image generated`;
            if (parsed.workflow_id)
              summary += ` (${parsed.workflow_id.slice(0, 8)}...)`;
            if (parsed.node_count) summary += ` · ${parsed.node_count} nodes`;
          } catch {
            if (result.length > 100) summary = `${result.slice(0, 100)}...`;
            else if (result) summary = result;
          }
          resolveToolCallOnLastMessage(cName, {
            summary: summary.trim() || undefined,
            url,
            failed,
          });
        },
        onDone: (_fullMessage, newConversationId, newMessageId) => {
          setActiveToolCall(null);
          settleRunningToolCalls();
          setMessageDone(assistantMessageId);
          setStreaming(false);
          if (newConversationId) setConversationId(newConversationId);
          if (newMessageId) setParentMessageId(newMessageId);
          // Refresh sidebar so the new/updated conversation appears
          refreshConversations();
        },
        onError: (error) => {
          setActiveToolCall(null);
          settleRunningToolCalls('failed');
          updateLastMessage(`\n\n**Error:** ${error}`);
          setMessageDone(assistantMessageId);
          setStreaming(false);
        },
        signal: abortControllerRef.current.signal,
      });
    },

    [
      currentAgent,
      isStreaming,
      conversationId,
      parentMessageId,
      attachedImages,
      attachedReferences,
      customInstructions,
      addMessage,
      updateLastMessage,
      addToolCallToLastMessage,
      resolveToolCallOnLastMessage,
      settleRunningToolCalls,
      setMessageDone,
      setStreaming,
      setActiveToolCall,
      setConversationId,
      setParentMessageId,
      refreshConversations,
    ],
  );

  const handleImageAttach = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      const newImages = imageFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setAttachedImages((prev) => [...prev, ...newImages]);
      // Reset input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [],
  );

  const removeImage = useCallback((index: number) => {
    setAttachedImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort();
    setActiveToolCall(null);
    // Aborting returns before onDone/onError, so nothing else settles the
    // rollup and its spinners would run forever.
    settleRunningToolCalls('failed');
    setStreaming(false);
    // Mark the last message as done
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.isStreaming) {
      setMessageDone(lastMsg.id);
    }
  }, [
    messages,
    setStreaming,
    setActiveToolCall,
    settleRunningToolCalls,
    setMessageDone,
  ]);

  const handleNewChat = useCallback(() => {
    handleStop();
    clearMessages();
    setAttachedReferences([]);
  }, [handleStop, clearMessages]);

  const [deletingConvoId, setDeletingConvoId] = useState<string | null>(null);

  const handleDeleteConversation = useCallback(
    async (convoId: string) => {
      if (!currentOrg?.orgid || !agentId) return;
      const wasActive = conversationId === convoId;
      removeConversation(convoId);
      setDeletingConvoId(null);
      try {
        await deleteConversation(currentOrg.orgid, agentId, convoId);
        if (wasActive) {
          handleNewChat();
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'delete-conversation', convoId },
        });
        refreshConversations();
        notification.error('Failed to delete conversation');
      }
    },
    [
      currentOrg?.orgid,
      agentId,
      conversationId,
      removeConversation,
      handleNewChat,
      refreshConversations,
      notification,
    ],
  );

  if (isLoading) {
    return (
      <div className='flex h-full'>
        {/* Chat history sidebar skeleton */}
        {showInlineHistory && (
          <div className='w-[252px] shrink-0 border-r bg-sidebar flex flex-col'>
            <div className='flex items-center gap-2 p-3'>
              <Skeleton className='size-6 rounded' />
              <Skeleton className='h-4 w-24' />
            </div>
            <div className='flex flex-col gap-2 px-3'>
              <Skeleton className='h-9 w-full rounded-lg' />
              <Skeleton className='h-8 w-full rounded-md' />
              <Skeleton className='h-8 w-3/4 rounded-md' />
              <Skeleton className='h-8 w-5/6 rounded-md' />
            </div>
          </div>
        )}
        {/* Main area skeleton */}
        <div className='flex flex-1 flex-col min-w-0 items-center justify-center gap-4 px-4 pb-6'>
          <div className='w-full max-w-3xl px-[20px] flex flex-col gap-4'>
            {/* Heading skeleton */}
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <Skeleton className='size-6 rounded' />
                <Skeleton className='h-5 w-40' />
              </div>
              <Skeleton className='h-8 w-80' />
            </div>
            {/* Input skeleton */}
            <Skeleton className='h-11 w-full rounded-3xl' />
            {/* Template text skeleton */}
            <Skeleton className='h-5 w-64' />
            {/* Template cards skeleton */}
            <div className='flex gap-4'>
              <Skeleton className='h-36 w-48 rounded-xl' />
              <Skeleton className='h-36 w-48 rounded-xl' />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /** Inline history panel (expanded or collapsed) — matches Figma sidebar design */
  const inlineHistoryPanel = showInlineHistory && (
    <>
      {isHistoryOpen ? (
        <div className='w-[252px] shrink-0 border-r bg-sidebar flex flex-col'>
          {/* Panel header — title + close */}
          <div className='flex items-center p-2 shrink-0'>
            <span className='text-sm font-medium text-sidebar-foreground flex-1 truncate'>
              {sidebarTitle ?? 'Chat History'}
            </span>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 shrink-0'
              onClick={() => setHistoryOpen(false)}
              title='Close panel'
            >
              <X className='size-4' />
            </Button>
          </div>

          {/* Sidebar content */}
          <div className='flex flex-1 flex-col gap-2 min-h-0 p-2 pt-0'>
            {/* New Chat button — SidebarMenuButton style */}
            <button
              type='button'
              onClick={handleNewChat}
              className='flex h-8 items-center gap-2 rounded-md px-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors w-full'
            >
              <Plus className='size-4 shrink-0' />
              <span className='truncate'>New Chat</span>
            </button>

            {/* Chat list */}
            <div className='flex flex-1 flex-col min-h-0'>
              {/* Section label — SidebarGroupLabel style */}
              <div className='flex h-8 items-center px-2'>
                <span className='text-sm font-medium text-sidebar-foreground'>
                  Your Chats
                </span>
              </div>

              {/* Conversation items */}
              <div className='flex-1 min-h-0 overflow-y-auto'>
                {isLoading ? (
                  <div className='space-y-1 px-2 py-1'>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className='h-8 w-full rounded-md' />
                    ))}
                  </div>
                ) : conversations.length === 0 ? (
                  <div className='px-2 py-8 text-center'>
                    <p className='text-sm text-muted-foreground'>
                      No conversations yet
                    </p>
                    <p className='text-xs text-muted-foreground/60 mt-1'>
                      Start chatting to see your history here
                    </p>
                  </div>
                ) : (
                  conversations.map((convo) => {
                    const isActive = conversationId === convo.conversationId;
                    const isConfirmingDelete =
                      deletingConvoId === convo.conversationId;
                    return (
                      <div
                        key={convo.conversationId}
                        className='group relative'
                      >
                        {isConfirmingDelete ? (
                          <div className='flex h-8 w-full items-center gap-1 rounded-md px-2 text-sm bg-sidebar-accent'>
                            <span className='truncate flex-1 text-left text-destructive'>
                              Delete?
                            </span>
                            <button
                              type='button'
                              onClick={() =>
                                handleDeleteConversation(convo.conversationId)
                              }
                              className='shrink-0 size-5 flex items-center justify-center rounded-sm text-destructive/70 hover:bg-destructive/10 hover:text-destructive'
                              title='Confirm delete'
                            >
                              <Check className='size-3.5' />
                            </button>
                            <button
                              type='button'
                              onClick={() => setDeletingConvoId(null)}
                              className='shrink-0 size-5 flex items-center justify-center rounded-sm text-muted-foreground hover:bg-sidebar-foreground/10 hover:text-foreground'
                              title='Cancel'
                            >
                              <X className='size-3.5' />
                            </button>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'flex h-8 w-full items-center rounded-md text-sm transition-colors',
                              'hover:bg-sidebar-accent',
                              isActive
                                ? 'bg-sidebar-accent font-medium text-sidebar-foreground'
                                : 'text-sidebar-foreground',
                            )}
                          >
                            <button
                              type='button'
                              onClick={() =>
                                handleSelectConversation(convo.conversationId)
                              }
                              className='flex-1 truncate text-left px-2 h-full'
                            >
                              {convo.title || 'Untitled'}
                            </button>
                            <button
                              type='button'
                              onClick={() =>
                                setDeletingConvoId(convo.conversationId)
                              }
                              className='shrink-0 size-5 flex items-center justify-center mr-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:bg-sidebar-foreground/10 hover:text-foreground'
                              aria-label={`Delete conversation ${convo.title || 'Untitled'}`}
                            >
                              <Trash2 className='size-3.5' />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Collapsed state — floating pill like Composer minimized state */
        <div className='absolute top-2 left-2 z-50'>
          <div
            className={cn(
              'px-2 py-2 rounded-xl shadow-sm',
              'bg-background border border-border',
              'flex items-center gap-2',
            )}
          >
            <span className='text-sm font-medium whitespace-nowrap'>
              {sidebarTitle ?? 'Chat History'}
            </span>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 shrink-0'
              onClick={() => setHistoryOpen(true)}
              title='Expand chat history'
            >
              <Maximize2 className='size-4' />
            </Button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className='flex h-full relative'>
      {/* Inline history panel (left side) */}
      {inlineHistoryPanel}

      {/* Main chat area */}
      <div className='flex flex-col flex-1 min-w-0 relative'>
        {/* Header actions rendered via portal into the layout header */}
        {document.getElementById('header-actions') &&
          createPortal(
            <>
              {!showInlineHistory && (
                <>
                  <Button variant='ghost' size='sm' onClick={handleNewChat}>
                    New Chat
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => setHistoryOpen(true)}
                    title='Chat history'
                  >
                    <History className='size-4' />
                  </Button>
                </>
              )}
              {showSettings && (
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => navigate(ROUTES.ADMIN.WORKFLOW_AGENT)}
                  title='Agent settings'
                >
                  <Settings className='size-4' />
                </Button>
              )}
            </>,
            document.getElementById('header-actions')!,
          )}

        {/* Hidden file input for image attachments */}
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          multiple
          className='hidden'
          onChange={handleImageAttach}
        />

        {isLoadingHistory ? (
          /* Loading state when switching conversations */
          <>
            <div className='flex-1 flex flex-col items-center justify-center gap-3 px-4 pb-6'>
              <Skeleton className='h-6 w-48 rounded' />
              <Skeleton className='h-4 w-72 rounded' />
            </div>
            <div className='shrink-0 pb-4'>
              <div className='mx-auto max-w-3xl px-4'>
                <Skeleton className='h-12 w-full rounded-2xl' />
              </div>
            </div>
          </>
        ) : messages.length === 0 ? (
          /* Empty state — greeting + input, scrollable when content overflows */
          <div className='flex-1 flex flex-col items-center justify-center px-4 pb-6'>
            {emptyStateHeading ?? (
              <>
                <h2 className='text-xl font-bold mb-2'>
                  {currentAgent?.name || 'Workflow Builder'}
                </h2>
                <p className='text-muted-foreground text-sm max-w-md mb-8 text-center'>
                  {currentAgent?.description ||
                    'Create automated AI workflows, query data sources, and connect to 800+ integrations.'}
                </p>
              </>
            )}
            <div className='w-full max-w-3xl'>
              {attachedImages.length > 0 && (
                <div className='flex gap-2 mb-2 flex-wrap'>
                  {attachedImages.map((img, idx) => (
                    <div
                      key={idx}
                      className='relative group w-16 h-16 rounded-md overflow-hidden border'
                    >
                      <img
                        src={img.preview}
                        alt={img.file.name}
                        className='w-full h-full object-cover'
                      />
                      <button
                        onClick={() => removeImage(idx)}
                        className='absolute top-0 right-0 bg-black/60 rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition-opacity'
                      >
                        <X className='h-3 w-3 text-white' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <ChatAgentInput
                onSubmit={handleSendMessage}
                onStop={handleStop}
                // onAttach={() => fileInputRef.current?.click()}
                disabled={isStreaming}
                isLoading={isStreaming}
                placeholder={
                  attachedImages.length > 0
                    ? 'Describe what to do with the image...'
                    : 'Create a workflow to research nuclear reactor supplychains and visualize them with a network graph'
                }
                autoFocus
                collectionReferences={collectionReferences}
                isLoadingCollections={isLoadingCollections}
                attachedReferences={attachedReferences}
                onAttachReference={attachReference}
                onRemoveReference={removeReference}
              />
              <div className='mt-3 px-1'>
                <CustomInstructionsPopover
                  customInstructions={customInstructions}
                  onSave={saveCustomInstructions}
                />
              </div>
              {emptyStateBelowInput}
            </div>
          </div>
        ) : (
          /* Chat with messages — content scrolls, input sticks to bottom */
          <>
            <div
              ref={scrollContainerCallbackRef}
              className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden'
            >
              <div className='max-w-3xl mx-auto px-4 py-6 pb-28 space-y-6'>
                {messages.map((msg) => {
                  const messageData: ChatMessageData = {
                    id: msg.id,
                    role: msg.role,
                    content: msg.content,
                    isStreaming: msg.isStreaming,
                    customInstructions: msg.customInstructions,
                    toolCalls: msg.toolCalls,
                    collections: msg.collections,
                  };
                  return (
                    <ChatMessageComponent
                      key={msg.id}
                      message={messageData}
                      collectionNames={collectionNames}
                    />
                  );
                })}
                {activeToolCall && (
                  <ToolProgressIndicator
                    toolName={activeToolCall.cleanName}
                    detail={activeToolCall.args}
                    startTime={activeToolCall.startTime}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
            {/* Input area — floating at bottom */}
            <div className='pointer-events-none absolute bottom-0 inset-x-0 z-10 pr-4'>
              {/* Scroll to bottom button */}
              {!isAtBottom && (
                <div className='flex justify-center pb-2'>
                  <button
                    type='button'
                    onClick={scrollToBottom}
                    className='pointer-events-auto flex size-8 items-center justify-center rounded-full border bg-background shadow-md transition-colors hover:bg-muted'
                    aria-label='Scroll to bottom'
                  >
                    <ArrowDown className='size-4 text-muted-foreground' />
                  </button>
                </div>
              )}
              <div className='pointer-events-auto max-w-3xl mx-auto px-4 pb-3'>
                {attachedImages.length > 0 && (
                  <div className='flex gap-2 mb-2 flex-wrap'>
                    {attachedImages.map((img, idx) => (
                      <div
                        key={idx}
                        className='relative group w-16 h-16 rounded-md overflow-hidden border'
                      >
                        <img
                          src={img.preview}
                          alt={img.file.name}
                          className='w-full h-full object-cover'
                        />
                        <button
                          onClick={() => removeImage(idx)}
                          className='absolute top-0 right-0 bg-black/60 rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition-opacity'
                        >
                          <X className='h-3 w-3 text-white' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <ChatAgentInput
                  onSubmit={handleSendMessage}
                  onStop={handleStop}
                  // onAttach={() => fileInputRef.current?.click()}
                  disabled={isStreaming}
                  isLoading={isStreaming}
                  placeholder={
                    attachedImages.length > 0
                      ? 'Describe what to do with the image...'
                      : 'Reply...'
                  }
                  autoFocus
                  collectionReferences={collectionReferences}
                  isLoadingCollections={isLoadingCollections}
                  attachedReferences={attachedReferences}
                  onAttachReference={attachReference}
                  onRemoveReference={removeReference}
                />
                <div className='mt-3 px-1'>
                  <CustomInstructionsPopover
                    customInstructions={customInstructions}
                    onSave={saveCustomInstructions}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sheet-based chat history sidebar (used when inline history is not enabled) */}
      {!showInlineHistory && (
        <ChatHistorySidebar
          open={isHistoryOpen}
          onOpenChange={setHistoryOpen}
          conversations={conversations}
          activeConversationId={conversationId}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          isLoading={isLoadingHistory}
        />
      )}
    </div>
  );
}
